import React from 'react';

import { WebsiteStyle } from '@cineamo/api/models/website-configuration/WebsiteStyle.types';

import { getPageBackgroundStyle } from '@cineamo/ui/src/helpers/website-style';

/**
 * The colour layer of the page background. Fixed and viewport-sized, so it stays put while the
 * page scrolls and the gradient always spans the viewport.
 */
export function PageBackgroundColorLayer({ style }: { style: WebsiteStyle }): React.JSX.Element {
    return (
        <div
            className="fixed left-0 top-0 w-screen h-dvh"
            style={getPageBackgroundStyle(style)}
        />
    );
}

/**
 * The cinema's background image on top of the colour layer, at its configured opacity.
 *
 * Driven entirely by the `--page-background*` variables the layouts set on the page root (see
 * `getCustomStyleVariables`), so it needs no configuration passed in. A cinema without a background
 * image leaves those variables unset, which makes every declaration here fall back to its initial
 * value — no image, no colour, fully transparent — and the layer is inert either way, so it is
 * rendered unconditionally rather than gated on a value read in JavaScript.
 */
export function PageBackgroundImageLayer(): React.JSX.Element {
    return (
        <div className="fixed left-0 top-0 w-screen h-screen pointer-events-none [background:var(--page-background)]">
            <div className="relative w-full h-full [background-image:var(--page-background-image)] [background-position:var(--page-background-position,center)] [background-repeat:var(--page-background-repeat)] [background-size:var(--page-background-size)] [opacity:var(--page-background-opacity)]" />
        </div>
    );
}

/**
 * The page background as a whole — the same two layers the layouts render, in the same order.
 *
 * Rendered inside a container that clips its subtree, this reproduces the page background within
 * that container: both layers are fixed and viewport-sized, so the strip showing through the
 * container continues the page background seamlessly, wherever the container currently sits. The
 * sticky date headers of the compact program list use it that way to hide the rows scrolling
 * behind them without an opaque colour of their own.
 */
export function PageBackgroundLayers({ style }: { style: WebsiteStyle }): React.JSX.Element {
    return (
        <>
            <PageBackgroundColorLayer style={style} />
            <PageBackgroundImageLayer />
        </>
    );
}
